import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  VideoLibrary as VideoLibraryIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

interface LatestAdCardProps {
  ad?: {
    ad_id: string;
    video_url: string;
    created_by: string;
    created_at: string;
    production_id: number;
    creative_number: number;
    ratio: string;
    duration: number;
    tags?: {
      cast?: string;
      heroAge?: string;
      heroGender?: string;
      licensedSong?: string[] | string;
      meantTo?: string[];
    };
    metadata?: {
      file_metadata?: {
        content_type?: string;
      };
      media_type?: string;
    };
  };
  loading?: boolean;
}

const LatestAdCard: React.FC<LatestAdCardProps> = ({ ad, loading }) => {
  if (loading) {
    return (
      <Card 
        sx={{ 
          height: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!ad) {
    return (
      <Card 
        sx={{ 
          height: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Latest Ad Upload
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            No ads uploaded yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const licensedSongs = ad.tags?.licensedSong 
    ? (Array.isArray(ad.tags.licensedSong) ? ad.tags.licensedSong : [ad.tags.licensedSong])
    : [];

  const mediaType = ad.metadata?.media_type?.toLowerCase() || '';
  const contentType = ad.metadata?.file_metadata?.content_type?.toLowerCase() || '';
  const urlPath = ad.video_url.split('?')[0].split('#')[0];
  const isHtml = mediaType === 'html' || contentType.startsWith('text/html') || /\.(html|htm)$/i.test(urlPath);
  const isImage = !isHtml && (mediaType === 'image' || contentType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(urlPath));

  return (
    <Card 
      sx={{ 
        height: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <VideoLibraryIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6">
            Latest Ad Upload
          </Typography>
        </Box>

        {/* Media Player/Viewer */}
        <Box 
          sx={{ 
            position: 'relative',
            maxHeight: 400,
            height: ad.ratio === '16:9' ? 300 : ad.ratio === '9:16' ? 400 : 350,
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isHtml ? (
            <iframe
              title={ad.ad_id || 'HTML creative'}
              src={ad.video_url}
              sandbox="allow-scripts allow-pointer-lock"
              style={{
                width: '100%',
                height: '100%',
                border: 0,
                backgroundColor: '#111',
              }}
            />
          ) : isImage ? (
            <img
              src={ad.video_url}
              alt={ad.ad_id}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <video
              src={ad.video_url}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>

        {/* Ad ID and Basic Info */}
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-all' }}>
          {ad.ad_id}
        </Typography>

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.8 }} />
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Uploaded by: <strong>{ad.created_by}</strong>
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.8 }} />
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {formatDate(ad.created_at)}
            </Typography>
          </Box>
        </Stack>

        {/* Tags */}
        {ad.tags && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, opacity: 0.9 }}>
              METADATA
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {ad.tags.cast && ad.tags.cast !== 'unknown' && (
                <Chip 
                  label={`Cast: ${ad.tags.cast}`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              )}
              {ad.tags.heroAge && ad.tags.heroAge !== 'unknown' && (
                <Chip 
                  label={`Age: ${ad.tags.heroAge}`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              )}
              {ad.tags.heroGender && ad.tags.heroGender !== 'unknown' && (
                <Chip 
                  label={`Gender: ${ad.tags.heroGender}`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              )}
              {licensedSongs.length > 0 && licensedSongs[0] && (
                <Chip 
                  label={`Song: ${licensedSongs[0]}`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              )}
              {ad.tags.meantTo && ad.tags.meantTo.length > 0 && (
                <Chip 
                  label={`Meant To: ${ad.tags.meantTo.length}`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LatestAdCard;

